/* Carousel behaviour for home-shelf.liquid (layout = scroll).
   ---------------------------------------------------------------------------
   1. Desktop hover-arrows for click-to-scroll. Mobile users finger-swipe.
   2. Click is ADDITIVE — each click adds one card-step to a running target,
      animation re-aims at the updated target. No clicks are dropped.
   3. Custom rAF scroll with 600ms cubic ease-out (≈ cubic-bezier(0.4,0,0.2,1),
      the Netflix/Material standard easing curve). Honours
      prefers-reduced-motion.
   4. INFINITE LOOP — at init we clone N cards from each end onto the
      opposite end and place the viewport at the start of the real cards.
      When the user scrolls past the real-region boundary into the cloned
      region, we silently shift scrollLeft by ±realWidth — they're now
      looking at the equivalent position in the real region (visually
      identical because the clones are identical). Feels continuous. */
(function () {
  if (window.__bcShelfNav) return;
  window.__bcShelfNav = true;

  var EASE_OUT_CUBIC = function (t) { return 1 - Math.pow(1 - t, 3); };
  var DURATION_MS = 600;
  var prefersReducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function rail(btn) { return btn.parentElement.querySelector('.home-shelf__grid--scroll'); }

  function cardStep(r) {
    var card = r.querySelector('.home-shelf-card');
    if (!card) return 200;
    var gap = parseFloat(getComputedStyle(r).columnGap || getComputedStyle(r).gap) || 16;
    return card.getBoundingClientRect().width + gap;
  }

  /* ---------- Infinite-loop cloning ------------------------------------- */

  function markClone(el, side) {
    el.dataset.bcClone = side;
    el.setAttribute('aria-hidden', 'true');
    el.querySelectorAll('a, button').forEach(function (n) { n.setAttribute('tabindex', '-1'); });
  }

  function measureRealBounds(r) {
    var real = r.querySelectorAll('.home-shelf-card:not([data-bc-clone])');
    if (!real.length) return;
    /* offsetLeft is relative to the rail's offsetParent. We want positions
       inside the rail itself, so we subtract rail.offsetLeft when both share
       the same offsetParent. In practice the cards sit inside the rail so
       offsetLeft is already rail-relative. */
    r.__bcRealStart = real[0].offsetLeft;
    var last = real[real.length - 1];
    r.__bcRealEnd = last.offsetLeft + last.offsetWidth;
    r.__bcRealWidth = r.__bcRealEnd - r.__bcRealStart;
  }

  function setupInfinite(r) {
    if (r.__bcInfInit) return;
    var cards = Array.from(r.querySelectorAll('.home-shelf-card:not([data-bc-clone])'));
    if (cards.length < 12) return; // too few cards to wrap nicely (anchor row)
    r.__bcInfInit = true;

    var CLONE_N = Math.min(6, Math.floor(cards.length / 4));

    // Append clones of the first N cards onto the tail
    for (var i = 0; i < CLONE_N; i++) {
      var c = cards[i].cloneNode(true);
      markClone(c, 'tail');
      r.appendChild(c);
    }
    // Prepend clones of the last N cards onto the head
    for (var j = 0; j < CLONE_N; j++) {
      var c2 = cards[cards.length - 1 - j].cloneNode(true);
      markClone(c2, 'head');
      r.insertBefore(c2, r.firstElementChild);
    }

    // Wait for layout to settle, then measure + position
    requestAnimationFrame(function () {
      measureRealBounds(r);
      // Start scrolled to the first real card
      r.scrollLeft = r.__bcRealStart;
      // Watch scroll for wrap triggers
      r.addEventListener('scroll', function () { checkWrap(r); }, { passive: true });
    });

    window.addEventListener('resize', function () { measureRealBounds(r); }, { passive: true });
  }

  function silentJump(r, delta) {
    /* Silent reposition — no animation, no smooth-scroll. Also shift the
       in-flight rAF anchors so the running animation continues seamlessly
       from the new position. */
    r.scrollLeft += delta;
    if (r.__bcTargetLeft != null) r.__bcTargetLeft += delta;
    if (r.__bcAnimStartLeft != null) r.__bcAnimStartLeft += delta;
  }

  function checkWrap(r) {
    if (r.__bcRealWidth == null) return;
    var sl = r.scrollLeft;
    if (sl >= r.__bcRealEnd) {
      // Entered tail clones — jump backwards by one real-width
      silentJump(r, -r.__bcRealWidth);
    } else if (sl < r.__bcRealStart) {
      // Entered head clones — jump forwards by one real-width
      silentJump(r, r.__bcRealWidth);
    }
  }

  /* ---------- Click → animated scroll ---------------------------------- */

  function animateScroll(el, dir) {
    var step = cardStep(el);

    if (el.__bcRafId == null) {
      el.__bcTargetLeft = el.scrollLeft + dir * step;
    } else {
      el.__bcTargetLeft = el.__bcTargetLeft + dir * step;
    }
    // Only clamp targets on non-infinite carousels (anchor row).
    if (!el.__bcInfInit) {
      var maxScroll = el.scrollWidth - el.clientWidth;
      el.__bcTargetLeft = Math.max(0, Math.min(maxScroll, el.__bcTargetLeft));
    }

    if (prefersReducedMotion) {
      el.scrollLeft = el.__bcTargetLeft;
      checkWrap(el);
      el.__bcRafId = null;
      return;
    }

    if (el.__bcRafId != null) cancelAnimationFrame(el.__bcRafId);

    if (el.__bcSnapBackup == null) el.__bcSnapBackup = el.style.scrollSnapType;
    el.style.scrollSnapType = 'none';

    el.__bcAnimStartLeft = el.scrollLeft;
    var deltaToGo = el.__bcTargetLeft - el.__bcAnimStartLeft;
    var startTime = null;

    function frame(now) {
      if (startTime === null) startTime = now;
      var t = Math.min((now - startTime) / DURATION_MS, 1);
      el.scrollLeft = el.__bcAnimStartLeft + deltaToGo * EASE_OUT_CUBIC(t);
      if (t < 1) {
        el.__bcRafId = requestAnimationFrame(frame);
      } else {
        el.scrollLeft = el.__bcTargetLeft;
        el.style.scrollSnapType = el.__bcSnapBackup || '';
        el.__bcSnapBackup = null;
        el.__bcRafId = null;
        el.__bcAnimStartLeft = null;
        checkWrap(el);
      }
    }
    el.__bcRafId = requestAnimationFrame(frame);
  }

  /* ---------- Edge detection (only for non-infinite anchor row) -------- */

  function updateEdges(r) {
    if (r.__bcInfInit) {
      // Infinite carousel — arrows always active
      r.parentElement.classList.remove('is-at-start', 'is-at-end');
      return;
    }
    var wrap = r.parentElement;
    var maxScroll = r.scrollWidth - r.clientWidth - 2;
    wrap.classList.toggle('is-at-start', r.scrollLeft <= 2);
    wrap.classList.toggle('is-at-end', r.scrollLeft >= maxScroll);
  }

  /* ---------- Wire-up --------------------------------------------------- */

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('.home-shelf__nav');
    if (!btn) return;
    var r = rail(btn);
    if (!r) return;
    animateScroll(r, btn.dataset.nav === 'next' ? 1 : -1);
  });

  function init() {
    var rails = document.querySelectorAll('.home-shelf__grid--scroll');
    rails.forEach(function (r) {
      setupInfinite(r);
      updateEdges(r);
      r.addEventListener('scroll', function () { updateEdges(r); }, { passive: true });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
